import { Link } from '@tanstack/react-router';
import { Bell, CheckCheck } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import { useState } from 'react';

import { useNotificationsList } from '@/shared/api/generated/notifications/notifications';
import { unwrap } from '@/shared/api/unwrap';
import { t } from '@/shared/i18n';
import { Button } from '@/shared/ui/Button';
import { Counter } from '@/shared/ui/Counter';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/Popover';
import { Skeleton } from '@/shared/ui/Skeleton';
import { Tooltip } from '@/shared/ui/Tooltip';

import { NotificationItem } from './NotificationItem';
import styles from './NotificationsBell.module.scss';
import { useInboxActions, useUnreadCount } from './useInbox';

interface Props {
  workspace: string;
}

/** Колокольчик в шапке: счётчик и поповер с последними уведомлениями. */
export function NotificationsBell({ workspace }: Props) {
  const [open, setOpen] = useState(false);
  const unread = useUnreadCount(workspace);
  const { markRead, markAllRead } = useInboxActions(workspace);
  const query = useNotificationsList(
    workspace,
    { limit: 10 },
    {
      query: { select: unwrap, enabled: open && workspace !== '' },
    },
  );
  const items = query.data?.items ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip content={t.nav.notifications}>
        <PopoverTrigger asChild>
          <button type="button" className={styles['bell']} aria-label={t.nav.notifications}>
            <Bell />
            <AnimatePresence>
              {unread > 0 && (
                <span className={styles['bell__count']}>
                  <Counter value={unread} tone="accent" max={99} />
                </span>
              )}
            </AnimatePresence>
          </button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent align="end" className={styles['popover']}>
        <div className={styles['head']}>
          <span className={styles['title']}>{t.inbox.title}</span>
          {unread > 0 && (
            <Button
              size="sm"
              variant="ghost"
              tone="neutral"
              iconLeft={<CheckCheck />}
              onClick={() => {
                void markAllRead();
              }}
            >
              {t.inbox.readAll}
            </Button>
          )}
        </div>

        <div className={styles['list']}>
          {query.isPending ? (
            <>
              <Skeleton height={44} />
              <Skeleton height={44} />
            </>
          ) : items.length === 0 ? (
            <p className={styles['empty']}>{t.inbox.empty}</p>
          ) : (
            items.map((item) => (
              <NotificationItem
                key={item.id}
                workspace={workspace}
                item={item}
                compact
                onOpen={(n) => {
                  setOpen(false);

                  if (!n.read) {
                    void markRead(n.id);
                  }
                }}
              />
            ))
          )}
        </div>

        <Link
          to="/app/w/$slug/inbox"
          params={{ slug: workspace }}
          className={styles['all']}
          onClick={() => {
            setOpen(false);
          }}
        >
          {t.inbox.open}
        </Link>
      </PopoverContent>
    </Popover>
  );
}
